import { Location } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Input,
  Renderer2,
  Signal,
  signal,
  WritableSignal,
} from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { RouterModule } from "@angular/router";
import Prism from "prismjs";

// EZ
import { DocMeta } from "@template-ui/decorators";
import { read, toggle } from "@template-ui/helpers";

import { ApplicationConfigurationService } from "@app/core/services/app-config.service";
import { AppAngularBaseClass } from "@app/core/services/app-angular-base";
import { LoadingService } from "../services/loading.service";
import { RoleService } from "../services/roles.service";
import { SettingsService } from "../services/settings.service";
import { EzFormDialogService } from "@template-ui/services";

/**
 * Base class for app feature components.
 *
 * Extend this class when a component needs one or more common app capabilities
 * already provided by the base chain (`AppAngularBaseClass` + this class):
 * - route/state helpers through `appState` (for example `appState.idSignal()`, `appState.cdcNumSignal()`)
 * - shared app services (`settings`, `loading`, `roles`, `ezDialog`, app config)
 * - common UI helpers (`read`, `toggle`, PDF trusted-url helper, Prism highlighting) for templates
 * - navigation-state reset behavior used by page-level views
 *
 * ## When to use
 * - Page/detail/shell components that participate in normal app routing and shared layout state.
 * - Components that need multiple shared services already exposed here.
 *
 * ## When not to use
 * - Pure/presentational leaf components that only render inputs/outputs.
 * - Small reusable widgets that should remain framework-agnostic or easy to unit test in isolation.
 *
 * In those cases, prefer a plain Angular component with explicit `inject(...)` calls
 * only for the dependencies it actually needs.
 */
@Component({
  selector: "ui-base, ez-base",
  imports: [RouterModule],
  template: ` <p>I am a base component, extend me!</p> `,
  styles: [],
})
@DocMeta({
  title: "Base",
  category: "Components",
})
export abstract class AppBaseComponent extends AppAngularBaseClass {
  // ----------------------------------------------------------------
  // Inputs, Outputs, Models, and Public Signals
  // ----------------------------------------------------------------
  /** Global dev-message signal shared across all AppBaseComponent instances. */
  static readonly devMessageSig = signal("");

  /**
   * @deprecated Use `this.appState.cdcNumSignal` directly in new code.
   */
  protected readonly cdcNumSignal = this.appState.cdcNumSignal;

  /**
   * @deprecated Use `this.appState.idSignal` directly in new code.
   */
  protected readonly idSignal = this.appState.idSignal;

  /** Signal exposed to templates for rendering temporary dev messages. */
  readonly devMessageSig = computed(() => AppBaseComponent.devMessageSig());

  /**
   * Allows parent components to override `cdcNum` from bindings.
   */
  @Input()
  set cdcNum(value: string | undefined) {
    this.cdcNumInput = value?.trim() || undefined;
  }

  get cdcNum(): string | undefined {
    return this.cdcNumInput || this.appState.cdcNumSignal() || undefined;
  }

  /**
   * Allows parent components to override route `id`.
   *
   * Named `safeId` to avoid collisions with existing `id` usage patterns.
   * @deprecated use this.appState.idSignal().
   */
  @Input()
  set safeId(value: string | undefined) {
    this.safeIdInput = value?.trim() || undefined;
  }

  /**
   * @deprecated use this.appState.idSignal().
   */
  get safeId(): string | undefined {
    return this.safeIdInput || this.appState.idSignal();
  }

  // ----------------------------------------------------------------
  // Dependency Injection
  // ----------------------------------------------------------------
  /** Prism syntax highlighter instance for child component templates. */
  protected readonly Prism = Prism;
  /** Angular sanitizer for trusted URL helpers. */
  protected readonly sanitizer: DomSanitizer = inject(DomSanitizer);
  /** Change detector reference for manual UI refresh scenarios. */
  protected readonly cd: ChangeDetectorRef = inject(ChangeDetectorRef);
  /** Host element reference. */
  protected readonly el: ElementRef<HTMLElement> = inject(ElementRef);
  /** Renderer used for safe DOM operations. */
  protected readonly renderer: Renderer2 = inject(Renderer2);
  /** Browser location helper. */
  protected readonly location: Location = inject(Location);

  /** Shared settings service. */
  protected readonly settings: SettingsService = inject(SettingsService);
  /** Shared loading service. */
  protected readonly loading: LoadingService = inject(LoadingService);
  /** Shared role service. */
  protected readonly roles: RoleService = inject(RoleService);
  /** App configuration service. */
  protected readonly appConfigService: ApplicationConfigurationService = inject(ApplicationConfigurationService);

  /** EZ Dialog Service */
  protected readonly ezDialog = inject(EzFormDialogService);

  // ----------------------------------------------------------------
  // Private/Internal State
  // ----------------------------------------------------------------
  /** Optional local `cdcNum` input override for component templates. */
  private cdcNumInput?: string;
  /** Optional local `safeId` input override for component templates. */
  private safeIdInput?: string;
  /** App configuration snapshot for convenient subclass access. */
  protected readonly appConfig = this.appConfigService.appConfig;
  /** NavigationStart signal used for navigation-state reset behavior. */
  private readonly appBaseNavigationStartSig = this.createNavigationStartSignal();
  /** Last tracked URL path (without fragment). */
  private lastUrl = "";
  /** Timeout handle for auto-clearing developer messages. */
  private devMsgTimeout: ReturnType<typeof setTimeout> | null = null;

  // ----------------------------------------------------------------
  // Lifecycle Hooks
  // ----------------------------------------------------------------
  constructor() {
    super();
    this.thisClassName = 'AppBaseComponent';

    effect(() => {
      const nav = this.appBaseNavigationStartSig();
      if (!nav) return;

      const current = this.stripFragment(nav.url);
      const previous = this.lastUrl;

      this.dlog(`Prev url: ${previous}, current url: ${current}`);

      if (!current.includes("not-found") && !!previous && current !== previous) {
        this.dlog("About to hide stuff");
        this.appState.showCaseSummaryBar.set(false);
        this.appState.commonHeaderExpanded.set(false);
        this.appState.summaryBarExpanded.set(false);
      }

      if (!current.includes("not-found")) {
        this.lastUrl = current;
      }
    });

  }

  /**
   * Highlights code blocks for subclasses that render Prism-enabled markup.
   */
  ngAfterViewInit(): void {
    this.Prism.highlightAll();
  }

  // ----------------------------------------------------------------
  // Public Methods
  // ----------------------------------------------------------------
  /**
   * Sets a temporary developer-only footer message.
   *
   * @param msg Message text or markup.
   * @param timeoutInMs Auto-clear timeout in milliseconds.
   */
  devMessage(msg: string, timeoutInMs: number = 3000): void {
    if (!this.roles.isDeveloper()) return;

    if (this.devMsgTimeout) {
      clearTimeout(this.devMsgTimeout);
      this.devMsgTimeout = null;
    }

    requestAnimationFrame(() => {
      AppBaseComponent.devMessageSig.set(`<span class="opacity-50">Dev msg: </span>${msg}`);
    });

    this.devMsgTimeout = setTimeout(() => {
      AppBaseComponent.devMessageSig.set("");
      this.devMsgTimeout = null;
    }, timeoutInMs);
  }

  /**
   * Returns a trusted URL suitable for embedding a PDF in the UI.
   *
   * @param arrayBuffer Binary PDF content.
   */
  getTrustedUrlForPdf(arrayBuffer: BlobPart): SafeResourceUrl {
    const blob = new Blob([arrayBuffer], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  /**
   * Toggles a writable boolean signal.
   * In here to be available to templates
   *
   * @param booleanSig Signal to toggle.
   */
  protected toggle(booleanSig: WritableSignal<boolean>): void {
    toggle(booleanSig);
  }

  /**
   * Reads either a raw value or a signal value.
   * In here to be available to templates
   * @param input Raw value or signal.
   */
  protected read<T>(input: T | Signal<T>): T {
    return read(input);
  }

  // ----------------------------------------------------------------
  // Private Helper Methods
  // ----------------------------------------------------------------
  /**
   * Removes the URL fragment from a route URL.
   *
   * @param url Route URL.
   */
  private stripFragment(url: string): string {
    const idx = url.indexOf("#");
    return idx === -1 ? url : url.slice(0, idx);
  }
}
